//@ts-nocheck
import { FILE_UPLOAD, SIGNABLE_DOCUMENT } from '../constants/passengers';

const normalizeFieldTypeKey = (fieldTypeKey) =>
  String(fieldTypeKey ?? '')
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[-\s]/g, '_')
    .toUpperCase();

export const FILE_UPLOAD_FIELD_TYPE_KEYS = [
  normalizeFieldTypeKey(FILE_UPLOAD),
  'FILE_UPLOAD'
];

export const SIGNABLE_DOCUMENT_FIELD_TYPE_KEYS = [
  normalizeFieldTypeKey(SIGNABLE_DOCUMENT),
  'SIGNABLE_DOCUMENT',
  'SIGNABLE_DOCUMENT_FIELD',
  'SIGNABLEDOCUMENT'
];

const getFieldTypeKeys = (fieldType = {}) => [
  fieldType?.key,
  fieldType?.value,
  fieldType?.fieldType,
  fieldType?.fieldtype,
  fieldType?.field_type
];

export const isFileUploadPassengerFieldType = (fieldType = {}) => {
  return getFieldTypeKeys(fieldType).some((fieldTypeKey) =>
    FILE_UPLOAD_FIELD_TYPE_KEYS.includes(normalizeFieldTypeKey(fieldTypeKey))
  );
};

export const isSignableDocumentPassengerFieldType = (fieldType = {}) => {
  return getFieldTypeKeys(fieldType).some((fieldTypeKey) =>
    SIGNABLE_DOCUMENT_FIELD_TYPE_KEYS.includes(
      normalizeFieldTypeKey(fieldTypeKey)
    )
  );
};

const normalizeTemplateId = (value) => String(value ?? '').trim();

const getFirstConfig = (config) => {
  if (Array.isArray(config)) {
    return config[0] ?? {};
  }

  return config ?? {};
};

export const hasPassengerSignableDocumentTemplate = (field = {}) => {
  const hasTemplateFlag =
    field?.hasdocumensotemplate ?? field?.hasDocumensoTemplate;

  if (
    hasTemplateFlag === true ||
    String(hasTemplateFlag).trim().toLowerCase() === 'true'
  ) {
    return true;
  }

  const tripConfig = getFirstConfig(field?.trippassengerfieldconfig);
  const passengerFieldConfig = getFirstConfig(
    field?.passengerfield?.passengerfieldconfig
  );

  return [
    field?.tripdocumensotemplateid,
    field?.tripDocumensoTemplateId,
    field?.passengerfielddocumensotemplateid,
    field?.passengerFieldDocumensoTemplateId,
    field?.documensotemplateid,
    field?.documensoTemplateId,
    tripConfig?.documensotemplateid,
    tripConfig?.documensoTemplateId,
    passengerFieldConfig?.documensotemplateid,
    passengerFieldConfig?.documensoTemplateId
  ].some((templateId) => Boolean(normalizeTemplateId(templateId)));
};

export const isPassengerFieldVisibleForBooking = (
  field = {},
  { enablePassengerFieldUploads = false, enablePaxWaivers = false } = {}
) => {
  const isFileUploadField = isFileUploadPassengerFieldType(field?.fieldtype);
  const isSignableDocumentField = isSignableDocumentPassengerFieldType(
    field?.fieldtype
  );

  return (
    (enablePassengerFieldUploads || !isFileUploadField) &&
    (enablePaxWaivers || !isSignableDocumentField) &&
    (!isSignableDocumentField || hasPassengerSignableDocumentTemplate(field))
  );
};

export const getPassengerFieldTypeLabel = (fieldType = {}, t) => {
  if (isFileUploadPassengerFieldType(fieldType)) {
    return t('passengers:fileUploadFieldType');
  }

  if (isSignableDocumentPassengerFieldType(fieldType)) {
    return t('passengers:signableDocumentFieldType');
  }

  return fieldType?.name ?? '-';
};

export const buildPassengerFieldTypeOptions = (
  fieldTypes = [],
  { enablePassengerFieldUploads = false, t }
) => {
  return (fieldTypes ?? [])
    .filter((fieldType) => {
      return (
        (enablePassengerFieldUploads ||
          !isFileUploadPassengerFieldType(fieldType)) &&
        !isSignableDocumentPassengerFieldType(fieldType)
      );
    })
    .map((fieldType) => {
      const label = getPassengerFieldTypeLabel(fieldType, t);
      const isFileUpload = isFileUploadPassengerFieldType(fieldType);
      const isSignableDocument =
        isSignableDocumentPassengerFieldType(fieldType);

      return {
        ...fieldType,
        allowoptions:
          isFileUpload || isSignableDocument ? false : fieldType?.allowoptions,
        id: fieldType.fieldtypeid,
        label,
        value: fieldType?.key
      };
    });
};

export const filterPassengerFieldUploads = (
  passengerFields = [],
  enablePassengerFieldUploads = false
) => {
  if (enablePassengerFieldUploads) {
    return passengerFields ?? [];
  }

  return (passengerFields ?? []).filter(
    (field) => !isFileUploadPassengerFieldType(field?.fieldtype)
  );
};

export const filterPassengerFieldWaivers = (
  passengerFields = [],
  enablePaxWaivers = false
) => {
  if (enablePaxWaivers) {
    return passengerFields ?? [];
  }

  return (passengerFields ?? []).filter(
    (field) => !isSignableDocumentPassengerFieldType(field?.fieldtype)
  );
};

const getFirstObject = (values = []) =>
  values.find((value) => value && typeof value === 'object') || null;

const normalizeBoolean = (value) =>
  value === true || String(value).toLowerCase() === 'true';

const getPassengerFieldOptions = (field = {}) =>
  field?.existingOptions ||
  field?.options ||
  field?.existingField?.options ||
  [];

export const getPassengerFieldValueRecord = (field = {}) => {
  const options = getPassengerFieldOptions(field);

  return (
    options.find(
      (option) =>
        option?.bookingpassengerfieldvalueid ||
        option?.bookingPassengerFieldValueId ||
        option?.value
    ) || null
  );
};

export const getPassengerSignableDocument = (field = {}) => {
  const valueRecord = getPassengerFieldValueRecord(field);

  return getFirstObject([
    field?.bookingPassengerSignedDocument,
    field?.bookingpassengersigneddocument,
    field?.signedDocument,
    field?.signeddocument,
    field?.passengerSignedDocument,
    field?.passengersigneddocument,
    field?.existingField?.bookingPassengerSignedDocument,
    field?.existingField?.bookingpassengersigneddocument,
    field?.existingField?.signedDocument,
    field?.existingField?.signeddocument,
    valueRecord?.bookingPassengerSignedDocument,
    valueRecord?.bookingpassengersigneddocument,
    valueRecord?.signedDocument,
    valueRecord?.signeddocument,
    field?.bookingPassengerSignedDocuments?.[0],
    field?.bookingpassengersigneddocuments?.[0],
    field?.signedDocuments?.[0],
    field?.signeddocuments?.[0]
  ]);
};

export const getPassengerSignableDocumentState = (field = {}) => {
  const valueRecord = getPassengerFieldValueRecord(field);
  const signedDocument = getPassengerSignableDocument(field);
  const existingValue = field?.existingValue || valueRecord?.value;
  const hasValueRecord = Boolean(
    valueRecord?.bookingpassengerfieldvalueid ||
    valueRecord?.bookingPassengerFieldValueId ||
    existingValue
  );
  const documentReady = normalizeBoolean(
    signedDocument?.documentReady ??
      signedDocument?.documentready ??
      signedDocument?.DocumentReady ??
      signedDocument?.document_ready
  );

  return {
    hasValueRecord,
    signedDocument,
    documentReady,
    isUnsigned: !hasValueRecord,
    isInProgress: hasValueRecord && !documentReady,
    externalSignedDocumentId:
      signedDocument?.externalSignedDocumentId ||
      signedDocument?.externalsigneddocumentid ||
      signedDocument?.externalDocumentId ||
      signedDocument?.externaldocumentid,
    token: signedDocument?.token || null,
    url: signedDocument?.url || signedDocument?.documentUrl || null
  };
};

export const getPassengerFieldUploadKey = (
  formId = '',
  trippassengerfieldid = ''
) => `${formId}:${trippassengerfieldid}`;

export const getPassengerFieldUploadFiles = (
  passengerFieldFiles = {},
  formId = '',
  trippassengerfieldid = ''
) =>
  passengerFieldFiles[
    getPassengerFieldUploadKey(formId, trippassengerfieldid)
  ] ?? [];

const isBrowserFile = (file) =>
  typeof File !== 'undefined' && file instanceof File;

const parsePassengerFieldFileValue = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);

    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const getPassengerFieldOptionFiles = (field = {}) => {
  return (field?.options ?? [])
    .flatMap((option) => parsePassengerFieldFileValue(option?.value))
    .filter(
      (file) => file?.fileId || file?.relativePath || file?.originalFilename
    );
};

export const getPassengerFieldFileName = (file = {}) => {
  if (typeof File !== 'undefined' && file instanceof File) {
    return file.name;
  }

  return (
    file?.originalFilename ||
    file?.originalfilename ||
    file?.filename ||
    file?.name ||
    file?.attachmentname ||
    file?.relativePath?.split('/').pop() ||
    file?.resourcepath?.split('/').pop() ||
    ''
  );
};

export const normalizePassengerFieldFile = (file = {}) => ({
  ...file,
  fileId: file?.fileId || file?.fileid || file?.fileReferenceId,
  originalFilename: getPassengerFieldFileName(file),
  contentType: file?.contentType || file?.contenttype || file?.type,
  size: Number(file?.size || file?.fileSize || file?.filesize || 0),
  uploadedAt: file?.uploadedAt || file?.uploadedat || file?.createdat,
  uploadedBy: file?.uploadedBy || file?.uploadedby || file?.uploader || '',
  showBadge: file?.isFromBooking || false
});

export const getExistingPassengerFieldFiles = (field = {}) => {
  const existingFileCollections = [
    field?.existingFiles,
    field?.existingfiles,
    getPassengerFieldOptionFiles(field),
    parsePassengerFieldFileValue(field?.value),
    field?.files,
    field?.attachments,
    field?.passengerfieldfiles,
    field?.trippassengerfieldfiles,
    field?.bookingpassengerfieldfiles,
    field?.passengerfieldvaluefiles,
    field?.trippassengerfieldvaluefiles
  ];

  const files = existingFileCollections.find(
    (files) =>
      Array.isArray(files) &&
      files.length > 0 &&
      files.some((file) => !isBrowserFile(file))
  );

  return files?.map(normalizePassengerFieldFile);
};

export const hasPreviouslyProvidedPassengerFieldFiles = (field = {}) =>
  Boolean(
    field?.hasfiles ||
    field?.hasFiles ||
    field?.previouslyprovided ||
    field?.previouslyProvided ||
    getExistingPassengerFieldFiles(field)?.length
  );

const hasPassengerFieldUploadValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value && typeof value === 'object') {
    return true;
  }

  if (typeof value !== 'string') {
    return Boolean(value);
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return false;
  }

  if (trimmedValue.startsWith('[')) {
    return parsePassengerFieldFileValue(trimmedValue).length > 0;
  }

  return true;
};

export const hasPassengerFieldUploadSelection = (
  field = {},
  uploadedFiles = []
) =>
  Boolean(
    (Array.isArray(uploadedFiles) && uploadedFiles.length) ||
    hasPassengerFieldUploadValue(field?.value) ||
    hasPreviouslyProvidedPassengerFieldFiles(field)
  );

export const getPassengerFieldUploadMetadata = (
  file,
  fileKey,
  sequence = 1
) => ({
  fileKey,
  name: file?.name,
  size: file?.size,
  type: file?.type,
  sequence
});

export const formatPassengerFieldFilesForExport = (files = []) => {
  const incomingFiles = Array.isArray(files) ? files : [];
  const normalizedFiles = incomingFiles
    .map(normalizePassengerFieldFile)
    .filter((file) => file?.originalFilename);

  if (!normalizedFiles.length) {
    return '';
  }

  const fileLabel = normalizedFiles.length === 1 ? 'file' : 'files';
  const filenames = normalizedFiles
    .map((file) => file.originalFilename)
    .join('; ');

  return `${normalizedFiles.length} ${fileLabel}: ${filenames}`;
};

export const fileToPassengerFieldUploadInput = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || '');

      resolve({
        originalFilename: file?.name,
        contentType: file?.type,
        size: file?.size,
        base64: result.includes(',') ? result.split(',').pop() : result
      });
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
};
